// W12 (the local-write quota rule, design W12-storage.md section 3.6): a quota error on a LOCAL write is
// never swallowed. The database's DBCore middleware (`db.ts`, `quotaMiddleware` below) reports every failed
// write here and rethrows it, so the screen that wrote still gets the error and its draft stays in memory.
// This module only remembers that it happened: the sync pill says `QUOTA_STATUS` and the Storage panel
// takes `lastWriteFailedForQuota`. Quil's own file (not canonical; bindr's `3480436` is the reference).
//
// It is EAGER (the middleware sits in `db.ts`), and the eager set is the binding budget, so the canonical
// `isQuotaError` and `QUOTA_STATUS` (`storageHealth.ts`, which also holds the panel model) are loaded on
// demand, the first time a write actually fails. The failure carries the canonical words, so nothing
// eager spells them. The error itself is rethrown at once and never waits for that import.
import { useSyncExternalStore } from 'react'
import type { DBCore } from 'dexie'

export interface QuotaFailure {
  at: number
  /** How many writes have failed for space since the last clear. */
  count: number
  /** The canonical `QUOTA_STATUS` words (read from `storageHealth.ts` when the first failure was judged). */
  status: string
}

let failure: QuotaFailure | null = null
/** The tables whose writes failed for space and have not been written to successfully since. */
const failedTables = new Set<string>()
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of [...listeners]) listener()
}

/** Judge a failed local write; record it when it was a quota error. Resolves true for a quota error. Never throws.
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export function noteLocalWriteError(err: unknown, now: number = Date.now(), tables: readonly string[] = []): Promise<boolean> {
  return import('./storageHealth').then(
    ({ isQuotaError, QUOTA_STATUS }) => {
      if (!isQuotaError(err)) return false
      failure = { at: now, count: (failure?.count ?? 0) + 1, status: QUOTA_STATUS }
      for (const t of tables) failedTables.add(t)
      emit()
      return true
    },
    () => false,
  )
}

export function getQuotaFailure(): QuotaFailure | null {
  return failure
}

/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export function clearQuotaFailure(): void {
  failedTables.clear()
  if (!failure) return
  failure = null
  emit()
}

/** A write that succeeds to every table that failed proves there is room again: the status goes away by itself. A write to some other table proves nothing. */
function noteWriteSucceeded(table: string): void {
  if (failedTables.delete(table) && failedTables.size === 0) clearQuotaFailure()
}

/** @internal Exported for its unit tests; production reaches it only from inside this module. */
export function subscribeQuotaFailure(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** The failure for the sync pill. */
export function useQuotaFailure(): QuotaFailure | null {
  return useSyncExternalStore(subscribeQuotaFailure, getQuotaFailure, getQuotaFailure)
}

/**
 * The DBCore middleware: one place that sees every write to the store. A quota failure may surface on
 * the request or, more often, when the transaction COMMITS (the transaction's own `abort`), so both are
 * watched. The error is always rethrown, never swallowed.
 */
export const quotaMiddleware = {
  stack: 'dbcore' as const,
  name: 'quil-quota',
  create: (down: DBCore): DBCore => ({
    ...down,
    transaction: (stores, mode, options) => {
      const tx = down.transaction(stores, mode, options)
      if (mode === 'readwrite') {
        const idb = tx as unknown as Partial<IDBTransaction>
        idb.addEventListener?.('abort', () => void noteLocalWriteError(idb.error, Date.now(), stores))
      }
      return tx
    },
    table: (tableName) => {
      const table = down.table(tableName)
      return {
        ...table,
        mutate: (req) =>
          table.mutate(req).then(
            (res) => {
              noteWriteSucceeded(tableName)
              return res
            },
            (error: unknown) => {
              void noteLocalWriteError(error, Date.now(), [tableName])
              throw error
            },
          ),
      }
    },
  }),
}
